import { useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import { apiUrl } from '../config/api';
import { readAuth, clearAuth } from './useAuthStorage';

/**
 * Add-to-cart logic shared by the product grid, the product page and
 * search results. Previously this exact function was copy-pasted into
 * five components, so a fix in one never reached the others.
 *
 * The request body is unchanged from the original implementation.
 */
const useCartActions = ({ onCartOpen, onLoginRequired } = {}) => {
  // Tracks which variant is mid-request so its button can show a
  // spinner and reject double submissions.
  const [pendingVariantId, setPendingVariantId] = useState(null);

  const addToCart = useCallback(
    async (variantId, productCode, quantity = 1) => {
      if (variantId == null) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'Select Variant!',
        });
        return false;
      }

      // Guard against a second click while the first is still in flight.
      if (pendingVariantId !== null) return false;

      const { token, user_id, common_id } = readAuth();
      setPendingVariantId(variantId);

      try {
        const response = await fetch(apiUrl('/api/cart/add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            common_id,
            user_id: parseInt(user_id, 10),
            product_code: productCode,
            quantity: parseInt(quantity, 10),
            variant_id: variantId,
          }),
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();

        if (data.full) {
          Swal.fire({
            icon: 'info',
            confirmButtonColor: '#a17e33',
            title: 'Maximum available quantity reached',
            text: 'You already have all the stock we have for this item in your cart.',
          });
        }

        if (data?.cart?.common_id) {
          localStorage.setItem('common_id', data.cart.common_id);
        }

        onCartOpen?.();
        return true;
      } catch (error) {
        // A signed-in user hitting a network error gets a message;
        // a stale/absent session is asked to log in instead.
        if (token || user_id) {
          Swal.fire({
            icon: 'error',
            confirmButtonColor: '#a17e33',
            title: 'Could not add to cart',
            text: 'Please check your connection and try again.',
          });
        } else {
          clearAuth();
          onLoginRequired?.();
        }
        return false;
      } finally {
        setPendingVariantId(null);
      }
    },
    [pendingVariantId, onCartOpen, onLoginRequired]
  );

  return { addToCart, pendingVariantId, isAdding: pendingVariantId !== null };
};

export default useCartActions;
